/*
 * Copyright (c) Arc XP 2025.
 */

import type { Deliverable } from '@wpmedia/swordfish-ts';
import { HlsVideoElement } from 'hls-video-element';
import { KoiAdsManager } from './ads';
import { AnalyticsConfig, KoiAnalytics } from './analytics';
import { PlayerUiOptions } from './player-ui-options';
import type { CaptionTrackData, KoiConfig, MediaData, MediaType, VideoData } from './types';
import './waveform';

const defaultSampleRate = 0.001;

// I clone this template into the koi's shadow DOM, to avoid parsing costs with `innerHTML`.
const styleTemplate = document.createElement('template');
styleTemplate.innerHTML = `
    <style>
      :host {
        display: block;
        width: 100%;
        max-width: 800px;
        margin: 0 auto;
      }
      media-controller {
        width: 100%;
        border-radius: var(--border-radius, 12px);
        overflow: hidden;
        background-clip: padding-box;
        background-color: rgba(255, 255, 255, 1);;
      }
      media-controller[audio] {
        width: auto;
        min-width: 300px;
        max-width: 100%;
      }
    </style>
    `;

/**
 * KoiPlayer is a custom web component. It is designed to fetch video or audio data and render a
 * media player using Media Chrome components. This component automatically handles initialization,
 * error handling, and rendering.
 */
class KoiPlayer extends HTMLElement {
  shadowRoot!: ShadowRoot;
  config?: KoiConfig;
  /** The type of media (video or audio) */
  type?: MediaType;
  /** UI elements to hide */
  hideOptions: string[];
  /** The media ID */
  mediaId?: string;
  /** The direct stream URL (optional) */
  stream?: string;
  private readonly creationTimestamp: number;
  #meta: {
    ready: boolean;
    live: boolean;
    showWaveform: boolean;
    captions: boolean;
    title?: string;
    sampleRate?: number;
  };
  #orgId?: string;
  #env: string;
  #adTagUrl?: string;
  #enableAds: boolean;
  #handleResize?: () => void;

  constructor() {
    super();
    this.creationTimestamp = Date.now();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot.appendChild(styleTemplate.content.cloneNode(true));
    this.#meta = {
      ready: false,
      live: false,
      showWaveform: false,
      captions: false,
    };
    this.hideOptions = [];
    this.#env = 'prod';
    this.#orgId = 'error';
    this.#enableAds = false;
  }

  private _adsManager?: KoiAdsManager;

  get adsManager(): KoiAdsManager | undefined {
    return this._adsManager;
  }

  private _analytics?: KoiAnalytics;

  get analytics(): KoiAnalytics | undefined {
    return this._analytics;
  }

  private _mediaElement?: HTMLMediaElement;

  get mediaElement(): HTMLMediaElement | undefined {
    return this._mediaElement;
  }

  private _mediaController?: HTMLElement;

  get mediaController(): HTMLElement | undefined {
    return this._mediaController;
  }

  get enableAds(): boolean {
    return this.#enableAds;
  }

  get adTagUrl(): string | undefined {
    return this.#adTagUrl;
  }

  get env(): string {
    return this.#env;
  }

  get orgId(): string | undefined {
    return this.#orgId;
  }

  get meta(): { ready: boolean; live: boolean; title?: string; sampleRate?: number } {
    return this.#meta;
  }

  /**
   * Fires when a <koi-player> element is inserted into the DOM.
   *
   * It's important to not override the page author, so we check if attributes are present.
   *
   * Attributes checked:
   *
   * - Type: Determines media type ('video' or 'audio').
   * - Media-id: Identifier for the media content.
   * - Stream: Direct URL for streaming media.
   * - Org-id: Organization ID for video API calls.
   * - Env: Environment for video API calls ('prod', 'dev', etc.).
   * - Ad-tag-url: URL for ad integration (video only).
   * - Enable-ads: Flag to enable ads (video only).
   * - Live: Flag to indicate live streaming mode.
   * - Hide: UI elements to hide in the player.
   *
   * Ensures that either 'media-id' or 'stream' is provided, but not both. Fetches and renders audio
   * or video data based on the type and attributes.
   */
  connectedCallback(): void {
    // Accessibility
    if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');

    // TODO: https://web.dev/custom-elements-best-practices/#make-properties-lazy
    this.type = (this.getAttribute('type') as MediaType) || 'video';
    this.hideOptions = this.getAttribute('hide') ? this.getAttribute('hide')!.split(',') : [];
    this.mediaId = this.getAttribute('media-id') || undefined;
    this.stream = this.getAttribute('stream') || undefined;
    if (this.hasAttribute('config')) {
      try {
        const configJson = this.getAttribute('config') || '{}';
        const config = JSON.parse(configJson);

        // config takes precedence
        if (config.type) this.type = config.type as MediaType;
        if (config.live !== undefined) this.#meta.live = Boolean(config.live);
        if (config.mediaId) this.mediaId = config.mediaId;
        if (config.stream) this.stream = config.stream;
        if (config.orgId) this.#orgId = config.orgId;
        if (config.env) this.#env = config.env;
        if (config.adTagUrl) this.#adTagUrl = config.adTagUrl;
        if (config.enableAds !== undefined) this.#enableAds = Boolean(config.enableAds);
        if (config.adTagUrl) this.#adTagUrl = config.adTagUrl;
        if (config.hideOptions)
          this.hideOptions = Array.isArray(config.hideOptions) ? config.hideOptions : [];

        // Audio only options
        if (this.type === 'audio') {
          if (config.waveform !== undefined) this.#meta.showWaveform = Boolean(config.waveform);
        }
      } catch (error) {
        console.error('KoiPlayer: Error parsing config attribute:', error);
      }
    }

    if (!this.type) {
      console.error('KoiPlayer: No type provided');
      this.renderError('No type provided');
      return;
    }

    if (this.mediaId && this.stream) {
      console.error('KoiPlayer: Both media-id and stream provided. Only one should be used.');
      this.renderError('Both media-id and stream provided. Only one should be used.');
      return;
    }

    if (this.stream) {
      const mediaData: MediaData = {
        mediaUrl: this.stream,
        title: this.getAttribute('title') || undefined,
      };
      this.renderPlayer(mediaData);
      this.#meta.ready = true;
      this.finalizeSetup(this.#meta.ready);
      return;
    }

    if (!this.mediaId) {
      console.error('KoiPlayer: No ID or stream provided');
      this.renderError('No ID or stream provided');
      return;
    }

    if (!this.#orgId) {
      console.error('KoiPlayer: Missing org-id or env attributes');
      this.renderError('Missing required attributes');
      return;
    }

    if (this.type === 'audio') {
      this.fetchAudioData(this.mediaId, this.#orgId, this.#env)
        .then(audioData => {
          this.#meta.ready = true;
          this.renderPlayer(audioData);
        })
        .catch(error => {
          console.error('KoiPlayer: Error fetching audio data', error);
          this.renderError('Error loading audio');
        });
    } else {
      this.fetchVideoData(this.mediaId, this.#orgId, this.#env)
        .then(videoData => {
          this.#meta.ready = true;
          this.renderPlayer(videoData);
        })
        .catch(error => {
          console.error('KoiPlayer: Error fetching video data', error);
          this.renderError('Error loading video');
        });
    }
    this.finalizeSetup(this.#meta.ready);
  }

  finalizeSetup(ready: boolean): void {
    if (typeof document.dispatchEvent === 'function') {
      if (ready) {
        document.dispatchEvent(
          new CustomEvent('koi-player-ready', {
            detail: { player: this },
          })
        );
      } else {
        document.dispatchEvent(
          new CustomEvent('koi-player-error', {
            detail: { player: this, error: 'Failed to initialize' },
          })
        );
      }
    }
  }

  /**
   * Fetches video data from the Video API
   *
   * @param mediaId - The video ID
   * @param orgId - The organization ID
   * @param env - The environment (e.g., 'prod', 'dev', 'staging')
   * @returns A promise that resolves to the video data
   * @throws Error if there's an error fetching the video data
   */
  async fetchVideoData(mediaId: string, orgId: string, env: string = 'prod'): Promise<MediaData> {
    try {
      const apiUrl = `https://${orgId}-${env}-cdn.video-api.arcpublishing.com/api/v1/ansvideos/findByUuid?uuid=${mediaId}`;

      const response = await fetch(apiUrl, {
        mode: 'cors',
        credentials: 'same-origin',
        referrerPolicy: 'no-referrer-when-downgrade',
      });

      if (!response || !response.ok) {
        return Promise.reject(
          new Error(
            `Failed to fetch video data: ${response ? `${response.status} ${response.statusText}` : 'No response received'}`
          )
        );
      }

      const data = await response.json();

      const videoData: VideoData = Array.isArray(data) && data.length > 0 ? data[0] : data;

      const mediaUrl =
        videoData.streams && videoData.streams.length > 0 ? videoData.streams[0].url : null;

      const thumbnailUrl =
        videoData.promo_items && videoData.promo_items.basic
          ? videoData.promo_items.basic.url
          : undefined;

      this.#meta.title =
        videoData.headlines && videoData.headlines.basic ? videoData.headlines.basic : 'Video';

      if (videoData.powaConfig && videoData.powaConfig.sampleRate !== undefined) {
        this.#meta.sampleRate = videoData.powaConfig.sampleRate;
      } else {
        this.#meta.sampleRate = defaultSampleRate;
      }

      //todo this is a mess
      const webVttTracks: CaptionTrackData[] = [];
      if (videoData.subtitles && videoData.subtitles.urls && videoData.subtitles.urls.length > 0) {
        // Video API only supports a single caption track right now.
        const rawTrack = videoData.subtitles.urls.find(track => track!.format === 'WEB_VTT'); // todo
        if (rawTrack) {
          webVttTracks.push({
            label: 'English',
            url: rawTrack.url,
            lang: 'en',
          });
        }
      }

      if (!mediaUrl) {
        return Promise.reject(new Error('No media URL found in video data'));
      }

      return {
        mediaUrl,
        thumbnailUrl: thumbnailUrl,
        title: videoData.headlines?.basic || 'Video',
        captionTracks: webVttTracks,
      };
    } catch (error) {
      console.error('KoiPlayer: Error fetching video data', error);
      const errorMessage = `Error loading video: ${(error as Error).message}`;
      return Promise.reject(new Error(errorMessage));
    }
  }

  /**
   * Fetches audio data from the API
   *
   * @param mediaId - The audio ID
   * @param orgId - The organization ID
   * @param env - The environment (e.g., 'prod', 'sandbox', etc.)
   * @param renditionKey - The rendition key (e.g., 'aac-standard')
   * @returns A promise that resolves to the audio data
   * @throws Error if there's an error fetching the audio data
   */
  async fetchAudioData(
    mediaId: string,
    orgId: string,
    env: string = 'prod',
    renditionKey: string = 'aac-standard'
  ): Promise<MediaData> {
    try {
      if (!mediaId || !orgId) {
        return Promise.reject(new Error('Media ID and Org ID are required for audio fetching'));
      }

      const baseUrl = this.getBaseAudioUrl(env, orgId);

      const audioFetchUrl = `${baseUrl}/${mediaId}?renditionKey=${renditionKey}&waveform=${this.#meta.showWaveform ? 'true' : 'false'}`;
      const response = await fetch(audioFetchUrl, {
        mode: 'cors',
        credentials: 'same-origin',
        referrerPolicy: 'no-referrer-when-downgrade',
      });

      if (!response || !response.ok) {
        return Promise.reject(
          new Error(
            `Failed to fetch audio data: ${response ? `${response.status} ${response.statusText}` : 'No response received'}`
          )
        );
      }

      const data: Deliverable = await response.json();
      if (!data.uri) {
        return Promise.reject(new Error('No URI found in the response'));
      }
      if (!data.waveform) {
        return { mediaUrl: data.uri };
      } else {
        return { mediaUrl: data.uri, waveform: data.waveform };
      }
    } catch (error) {
      console.error('KoiPlayer: Error fetching audio data', error);
      const errorMessage = `Error loading audio: ${(error as Error).message}`;
      return Promise.reject(new Error(errorMessage));
    }
  }

  /**
   * Renders the media player in the shadow DOM
   *
   * @param mediaData - The media data object
   */
  renderPlayer(mediaData: MediaData): void {
    const posterPlaceholder =
      'data:image/svg+xml;charset=utf-8,%3Csvg xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22 viewBox%3D%220 0 1920 1080%22%3E%3Crect width%3D%221920%22 height%3D%221080%22 fill%3D%22%23333%22%2F%3E%3C%2Fsvg%3E';

    this._mediaController = document.createElement('media-controller');
    if (this.type === 'audio') {
      this._mediaController.setAttribute('audio', '');

      const audio = document.createElement('audio');
      audio.setAttribute('slot', 'media');
      audio.setAttribute('src', mediaData.mediaUrl);
      // TODO(eziegler): CORS requests for audio files
      // https://arcpublishing.atlassian.net/browse/DAM-11936
      // audio.setAttribute('crossorigin', '');
      this._mediaController.appendChild(audio);

      if (mediaData.waveform) {
        const waveform = document.createElement('koi-waveform');
        waveform.setAttribute('data', JSON.stringify(mediaData.waveform));
        this._mediaController.appendChild(waveform);
      }
    } else {
      this._mediaController.setAttribute('defaultsubtitles', '');
      const hlsVideo = document.createElement('hls-video') as HTMLElement;
      hlsVideo.setAttribute('slot', 'media');
      hlsVideo.setAttribute('src', mediaData.mediaUrl);
      hlsVideo.setAttribute('poster', mediaData.thumbnailUrl || posterPlaceholder);
      hlsVideo.setAttribute('title', mediaData.title || 'Video');
      hlsVideo.setAttribute('crossorigin', '');
      this._mediaController.appendChild(hlsVideo);

      if (mediaData.captionTracks && mediaData.captionTracks.length > 0) {
        const tracks = this.buildCaptionTracks(mediaData.captionTracks);
        for (const track of tracks) {
          hlsVideo.appendChild(track);
        }

        this.#meta.captions = true;
      }

      const loadingIndicator = document.createElement('media-loading-indicator');
      loadingIndicator.setAttribute('slot', 'centered-chrome');
      loadingIndicator.setAttribute('noautohide', '');
      this._mediaController.appendChild(loadingIndicator);

      if (mediaData.thumbnailUrl) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.referrerPolicy = 'no-referrer-when-downgrade';
        img.onload = () => {
          hlsVideo.setAttribute('poster', mediaData.thumbnailUrl!);
        };
        img.onerror = () => {
          console.warn('Failed to load thumbnail image, using placeholder');
        };
        img.src = mediaData.thumbnailUrl;
      }
    }

    const controlBar = document.createElement('media-control-bar');

    const controlElements = this.generateControlElements();
    controlBar.appendChild(controlElements);

    this._mediaController.appendChild(controlBar);

    this.shadowRoot.appendChild(this._mediaController);

    if (this._mediaController && typeof this._mediaController.querySelector === 'function') {
      if (this.type === 'audio') {
        const audioElement = this._mediaController.querySelector('audio');
        if (audioElement) {
          this._mediaElement = audioElement;
        }
      } else {
        const wrapperElement = this._mediaController.querySelector('hls-video') as HlsVideoElement;
        if (wrapperElement) {
          this._mediaElement = wrapperElement.nativeEl;
        }
      }
    }

    // Only initialize analytics when not using direct stream
    if (!this.stream) {
      this.initializeAnalytics();
    }

    this.initializeAdsManager();
  }

  /**
   * Generates control elements based on hide options
   *
   * @returns A document fragment containing the control elements
   */
  generateControlElements(): DocumentFragment {
    const fragment = document.createDocumentFragment();

    const controls = PlayerUiOptions.getDefaultControls(
      this.type,
      this.#meta.live,
      this.#meta.captions
    );

    controls.forEach(control => {
      if (this.shouldHideElement(control.name)) {
        return;
      }

      const element = document.createElement(control.elementName);

      Object.entries(control.attributes).forEach(([key, value]) => {
        // If the attribute value is a function, call it to get the actual value
        const attrValue = typeof value === 'function' ? value() : value;
        element.setAttribute(key, attrValue);
      });

      fragment.appendChild(element);
    });

    return fragment;
  }

  /**
   * Renders an error message in the shadow DOM
   *
   * @param message - The error message to display
   */
  renderError(message: string): void {
    while (this.shadowRoot.firstChild) {
      this.shadowRoot.removeChild(this.shadowRoot.firstChild);
    }

    const errorContainer = document.createElement('div');
    errorContainer.style.color = 'red';
    errorContainer.style.padding = '20px';
    errorContainer.style.textAlign = 'center';

    const errorMessage = document.createElement('p');
    errorMessage.textContent = `Error: ${message}`;

    errorContainer.appendChild(errorMessage);
    this.shadowRoot.appendChild(errorContainer);
  }

  /**
   * Determines if a specific UI element should be hidden
   *
   * @param elementType - The type of UI element to check
   * @returns True if the element should be hidden, false otherwise
   */
  shouldHideElement(elementType: string): boolean {
    if (!this.hideOptions || !this.hideOptions.length) return false;
    return this.hideOptions.includes(elementType);
  }

  /** Clean up resources when the element is removed from the DOM */
  disconnectedCallback(): void {
    if (this._adsManager) {
      this._adsManager.destroy();
      this._adsManager = undefined;
    }

    if (this.#handleResize) {
      window.removeEventListener('resize', this.#handleResize);
      this.#handleResize = undefined;
    }
  }

  private getBaseAudioUrl(env: string, orgId: string) {
    switch (env) {
      case 'prod':
        return `https://${orgId}.arcpublishing.com/audiocenter/public/media/audio`;
      case 'dev':
        return 'https://swordfish-api-dev-us-east-1.video.aws.arc.pub/public/media/audio';
      default:
        return `https://${env}.${orgId}.arcpublishing.com/audiocenter/public/media/audio`;
    }
  }

  private initializeAnalytics() {
    if (this._adsManager) {
      throw new Error('KoiPlayer: Analytics should be initialized before ads manager');
    }

    if (!this.orgId || !this.mediaId) {
      throw new Error('KoiPlayer: orgId and mediaId are required for analytics');
    }

    const config: AnalyticsConfig = {
      mediaElement: this._mediaElement!,
      player_init_time: this.creationTimestamp,
      orgId: this.#orgId!,
      env: 'sandbox', //todo
      mediaId: this.mediaId!,
      type: this.type!,
      meta: this.#meta,
      debug: true,
    };
    this._analytics = new KoiAnalytics(config);
    this._analytics.init();
  }

  /**
   * Initializes the ads manager for the media player.
   *
   * This method sets up the ads manager if ads are enabled and a valid ad tag URL is provided. It
   * also configures event listeners for triggering ad playback and handling window resizing to
   * adjust the ads display.
   */
  private initializeAdsManager() {
    if (this.#enableAds && this.#adTagUrl) {
      if (this._mediaElement) {
        this._adsManager = new KoiAdsManager();
        this._adsManager.init(this._mediaElement as HTMLVideoElement, this.#adTagUrl);

        if (this._mediaController) {
          const playButton = this._mediaController.querySelector('media-play-button');
          if (playButton) {
            playButton.addEventListener('click', () => {
              if (this._adsManager && !this._adsManager.isAdPlaying) {
                // TODO: decouple this by using the KoiEventBus
                this._adsManager.playAds();
              }
            });
          }
        }

        this.#handleResize = () => {
          if (this._adsManager) {
            this._adsManager.resize();
          }
        };
        window.addEventListener('resize', this.#handleResize);
      }
    }
  }

  private buildCaptionTracks(captionTracks: CaptionTrackData[]): HTMLElement[] {
    const tracks: HTMLElement[] = [];
    captionTracks.forEach(track => {
      const trackElement = document.createElement('track');
      trackElement.setAttribute('kind', 'captions');
      trackElement.setAttribute('label', track.label);
      trackElement.setAttribute('srclang', track.lang || 'en');
      trackElement.setAttribute('src', track.url);
      tracks.push(trackElement);
    });

    return tracks;
  }
}

export default KoiPlayer;
